<template>
  <div class="bcard" :class="`bcard--${banner.type}`" dir="rtl">
    <!-- Image (image / image_text) -->
    <div v-if="banner.type !== 'text' && banner.image" class="bcard__media">
      <img :src="banner.image" :alt="banner.title || 'إعلان'" loading="lazy" decoding="async" />
    </div>

    <!-- Text (text / image_text) -->
    <div v-if="banner.type !== 'image'" class="bcard__body">
      <h3 v-if="banner.title" class="bcard__title">{{ banner.title }}</h3>
      <p v-if="banner.description" class="bcard__desc">{{ banner.description }}</p>
      <button
        v-if="banner.button_text && banner.button_url"
        type="button"
        class="bcard__cta"
        @click="$emit('cta', banner.button_url)"
      >
        {{ banner.button_text }}
        <i class="pi pi-arrow-left" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Image-only banners can still be clickable when a URL is set -->
    <button
      v-if="banner.type === 'image' && banner.button_url"
      type="button"
      class="bcard__overlay-link"
      :aria-label="banner.button_text || 'فتح'"
      @click="$emit('cta', banner.button_url)"
    ></button>
  </div>
</template>

<script setup>
defineProps({ banner: { type: Object, required: true } });
defineEmits(['cta']);
</script>

<style scoped>
.bcard {
  position: relative; display: flex; gap: 1rem; align-items: center; overflow: hidden;
  border-radius: 18px; background: linear-gradient(135deg, #eef2ff, #f5f3ff);
  border: 1px solid rgba(91,95,239,.14); box-shadow: 0 6px 22px rgba(17,24,39,.06);
  min-height: 150px;
}
/* Image only: let the image fill the card cleanly. */
.bcard--image { padding: 0; background: #0f172a; }
.bcard--image .bcard__media { flex: 1 1 100%; align-self: stretch; }
.bcard--image .bcard__media img { width: 100%; height: 100%; max-height: 320px; object-fit: cover; }

.bcard--text { padding: 1.6rem 1.8rem; }

.bcard--image_text .bcard__media { flex: 0 0 40%; align-self: stretch; }
.bcard__media { min-height: 150px; }
.bcard__media img { width: 100%; height: 100%; object-fit: cover; display: block; }

.bcard__body { flex: 1; padding: 1.4rem 1.6rem; min-width: 0; }
.bcard--image_text .bcard__body { padding-inline-start: 0; }
.bcard__title { font-size: clamp(1.05rem, 2.4vw, 1.4rem); font-weight: 800; color: #111827; margin: 0 0 .45rem; line-height: 1.35; }
.bcard__desc { font-size: .92rem; color: #4b5563; line-height: 1.7; margin: 0 0 .9rem; }
.bcard__cta {
  display: inline-flex; align-items: center; gap: .4rem; border: 0; cursor: pointer;
  background: #5B5FEF; color: #fff; padding: .55rem 1.25rem; border-radius: 12px;
  font-family: inherit; font-weight: 700; font-size: .88rem; transition: transform .2s ease, background .2s ease;
}
.bcard__cta:hover { background: #4549d8; transform: translateY(-1px); }
.bcard__overlay-link { position: absolute; inset: 0; background: transparent; border: 0; cursor: pointer; }

@media (max-width: 768px) {
  .bcard { flex-direction: column; align-items: stretch; gap: 0; min-height: 0; }
  .bcard--image_text .bcard__media { flex-basis: auto; }
  .bcard__media { min-height: 0; }
  .bcard--image_text .bcard__media img { max-height: 200px; }
  .bcard__body { padding: 1.1rem 1.2rem; }
  .bcard--text { padding: 1.3rem 1.2rem; }
}

/* ── Desktop / tablet (≥769px): image-only hero banner ──
   The mobile-first base rules force the image to height:100% + object-fit:cover
   against the card's small min-height (150px), which cropped the image top/bottom
   on wide screens. On desktop we instead let the image drive its own height so the
   FULL image shows at its true aspect ratio — no crop, no stretch, no distortion.
   The .banner-wrap max-width (1200px) bounds the width, so the banner never grows
   excessively tall on very wide screens. Mobile (≤768px) is intentionally untouched. */
@media (min-width: 769px) {
  .bcard--image { min-height: 0; }
  .bcard--image .bcard__media { align-self: auto; }
  .bcard--image .bcard__media img {
    width: 100%;
    height: auto;         /* natural aspect ratio → whole image visible */
    max-height: none;     /* drop the 320px cap that forced cover-cropping */
    object-fit: contain;  /* never crop / never distort (guard if a height is set) */
  }
}
</style>
